import type {
  AccountCreationContext,
  OAuthHandlerContext,
  OAuthStatusCheckResult,
  Strategy
} from '@/features/auth/login/types/login';
import { OAUTH_STATUS } from '@/features/auth/shared/consts/clerk';
import { extractClerkErrorMessage } from '@/features/auth/shared/utils/extractClerkError';
import { toast } from 'sonner';

import { initiateOAuthRedirect } from './oauthRedirect';

const handleOAuthSessionComplete = async (
  sessionId: string,
  context: OAuthHandlerContext,
  successMessageKey: 'success.auth.login' | 'success.auth.signup'
): Promise<void> => {
  const { setActive, navigate, setError, t, setOauthLoading } = context;

  if (!setActive) {
    setError({ type: 'email', message: t('error.login.unknown') });
    setOauthLoading(false);
    return;
  }

  try {
    await setActive({ session: sessionId });
    toast.success(t(successMessageKey));
    navigate('/');
  } catch {
    setError({ type: 'email', message: t('error.login.unknown') });
  } finally {
    setOauthLoading(false);
  }
};

export const handleOAuthAccountCreation = async (
  strategy: Strategy,
  context: AccountCreationContext
): Promise<void> => {
  const { signUp, setIsCreatingAccount, setError, t, setOauthLoading } = context;

  setIsCreatingAccount(true);

  try {
    await initiateOAuthRedirect(signUp, strategy);
  } catch (signUpErr) {
    setIsCreatingAccount(false);
    const signUpErrorMessage = extractClerkErrorMessage(signUpErr);
    const mappedError = {
      type: 'email' as const,
      message: signUpErrorMessage ?? t('error.login.accountNotFound')
    };
    setError(mappedError);
    toast.error(mappedError.message);
  } finally {
    setOauthLoading(false);
  }
};

export const processOAuthStatus = async (
  status: OAuthStatusCheckResult,
  handlerContext: OAuthHandlerContext,
  accountCreationContext?: AccountCreationContext
): Promise<boolean> => {
  const { navigate, t, setOauthLoading } = handlerContext;

  switch (status.status) {
    case OAUTH_STATUS.IDLE:
    case OAUTH_STATUS.PROCESSING:
      return false;

    case OAUTH_STATUS.SIGN_IN_COMPLETE:
      if (status.sessionId) {
        await handleOAuthSessionComplete(status.sessionId, handlerContext, 'success.auth.login');
      }
      return true;

    case OAUTH_STATUS.SIGN_UP_COMPLETE:
      if (status.sessionId) {
        await handleOAuthSessionComplete(status.sessionId, handlerContext, 'success.auth.signup');
      }
      return true;

    case OAUTH_STATUS.NEEDS_ACCOUNT_CREATION:
      if (status.strategy && accountCreationContext) {
        await handleOAuthAccountCreation(status.strategy, accountCreationContext);
      }
      return true;

    case OAUTH_STATUS.ALREADY_SIGNED_IN:
      toast.success(t('success.auth.sessionExists'));
      navigate('/');
      setOauthLoading(false);
      return true;

    case OAUTH_STATUS.OAUTH_ERROR:
      toast.error(status.errorMessage ?? t('error.login.oauthCanceled'));
      setOauthLoading(false);
      return true;

    default:
      toast.error(t('error.login.unknown'));
      setOauthLoading(false);
      return true;
  }
};
